import React, {ComponentType, CSSProperties, ReactNode, Ref} from 'react';

import Select, {Creatable} from 'react-select';

import type {ValueType} from 'react-select';

export const themeModifier = (theme: Theme): Theme => ({
  ...theme,
  colors: {...theme.colors, primary: '#4deaff'},
  spacing: {...theme.spacing, menuGutter: 0},
});

export const SelectOptionStyles = <IT, V>(
  originalStyles: CSSProperties,
  {isFocused, isSelected}: OptionProps<IT, V>
): CSSProperties => {
  let styles = {
    ...originalStyles,
    color: '#666666',
    cursor: 'pointer',
    display: 'block',
  };

  if (isSelected) {
    styles = {
      ...styles,
      background: '#f0f0f0 linear-gradient(180deg,#f3f3f3,#efefef) repeat-x',
    };
  }

  if (isFocused) {
    styles = {
      ...styles,
      background: '#00b4cc linear-gradient(180deg,#26bfd4,#00b4cc) repeat-x',
      color: '#fff',
    };
  }

  return styles;
};

export const SelectMenuListStyles = (
  originalStyles: CSSProperties
): CSSProperties => ({
  ...originalStyles,
  paddingTop: 0,
  boxShadow: '0 1px 1px rgba(0,0,0,0.2)',
});

export const SelectMenuStyles = (
  originalStyles: CSSProperties
): CSSProperties => ({
  ...originalStyles,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  zIndex: 5,
});

export const ControlStyles = (
  originalStyles: CSSProperties,
  {selectProps: {menuIsOpen}, isFocused}: any
): CSSProperties => ({
  ...originalStyles,
  boxShadow: `inset 0 1px 1px rgba(0,0,0,.075)${
    isFocused ? ', 0 0 0 0.2rem rgba(0,180,204,.25)' : ''
  }`,
  transition: 'border-color ease-in-out .15s,box-shadow ease-in-out .15s',
  borderBottomLeftRadius: menuIsOpen ? 0 : originalStyles.borderRadius,
  borderBottomRightRadius: menuIsOpen ? 0 : originalStyles.borderRadius,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

export const selectStyles = {
  option: SelectOptionStyles,
  menu: SelectMenuStyles,
  menuList: SelectMenuListStyles,
  control: ControlStyles,
} as const;

export interface OptionType<IT, V, L = ReactNode> {
  data: IT;
  value: V;
  label: L;
}
export type OptionsType<IT, V = IT, L = ReactNode> = Array<
  OptionType<IT, V, L>
>;

export type FocusEventHandler = (event: React.FocusEvent<HTMLElement>) => void;
export type MouseEventHandler = (event: React.MouseEvent<HTMLElement>) => void;

export type KeyboardEventHandler = (
  event: React.KeyboardEvent<HTMLElement>
) => void;
export interface PropsWithInnerRef {
  innerRef: Ref<any>;
}

interface ThemeSpacing {
  baseUnit: number;
  controlHeight: number;
  menuGutter: number;
}
export interface Theme {
  borderRadius: number;
  colors: Record<string, string>;
  spacing: ThemeSpacing;
}

export type ActionTypes =
  | 'select-option'
  | 'remove-value'
  | 'deselect-option'
  | 'pop-value'
  | 'set-value'
  | 'clear'
  | 'create-option';

export type ActionMeta<IT> =
  | {
      action: 'select-option';
      option: IT;
    }
  | {
      action: 'remove-value';
      removedValue: IT;
    }
  | {
      action:
        | 'deselect-option'
        | 'pop-value'
        | 'set-value'
        | 'clear'
        | 'create-option';
    };

export type InputActionTypes =
  | 'set-value'
  | 'input-change'
  | 'input-blur'
  | 'menu-close';

export interface InputActionMeta {
  action: InputActionTypes;
}

export type MenuPlacement = 'auto' | 'bottom' | 'top';
export type MenuPosition = 'absolute' | 'fixed';

export interface ExtraPropsType<IT> {
  inputText?: string;
  hasNext?: boolean;
  loadNext?: () => unknown;
  waiting?: boolean;
  loadingLast?: boolean;
  onLimit?: boolean;
  selectLimitText?: ReactNode;
  typeIcon?: null | string | ((value: IT) => string | null);
  canInvite?: boolean;
  onInvite?: (email: string) => void;
}

export interface OptionProps<IT, V> extends PropsWithInnerRef {
  data: IT;
  id: number;
  index: number;
  isDisabled: boolean;
  isFocused: boolean;
  isSelected: boolean;
  label: string;
  onClick: MouseEventHandler;
  onMouseOver: MouseEventHandler;
  value: V;
}

export interface ComponentStyle {
  clearIndicator: (style: CSSProperties, state: any) => CSSProperties;
  container: (style: CSSProperties, state: any) => CSSProperties;
  control: (style: CSSProperties, state: any) => CSSProperties;
  dropdownIndicator: (style: CSSProperties, state: any) => CSSProperties;
  group: (style: CSSProperties, state: any) => CSSProperties;
  groupHeading: (style: CSSProperties, state: any) => CSSProperties;
  indicatorsContainer: (style: CSSProperties, state: any) => CSSProperties;
  indicatorSeparator: (style: CSSProperties, state: any) => CSSProperties;
  input: (style: CSSProperties, state: any) => CSSProperties;
  loadingIndicator: (style: CSSProperties, state: any) => CSSProperties;
  loadingMessageCSS: (style: CSSProperties, state: any) => CSSProperties;
  menu: (style: CSSProperties, state: any) => CSSProperties;
  menuList: (style: CSSProperties, state: any) => CSSProperties;
  menuPortal: (style: CSSProperties, state: any) => CSSProperties;
  multiValue: (style: CSSProperties, state: any) => CSSProperties;
  multiValueLabel: (style: CSSProperties, state: any) => CSSProperties;
  multiValueRemove: (style: CSSProperties, state: any) => CSSProperties;
  noOptionsMessageCSS: (style: CSSProperties, state: any) => CSSProperties;
  option: (style: CSSProperties, state: any) => CSSProperties;
  placeholder: (style: CSSProperties, state: any) => CSSProperties;
  singleValue: (style: CSSProperties, state: any) => CSSProperties;
  valueContainer: (style: CSSProperties, state: any) => CSSProperties;
}
export type StylesConfig = Partial<ComponentStyle>;

export interface SelectComponents {
  ClearIndicator: ComponentType<any> | null;
  Control: ComponentType<any>;
  DropdownIndicator: ComponentType<any> | null;
  DownChevron: ComponentType<any>;
  CrossIcon: ComponentType<any>;
  Group: ComponentType<any>;
  GroupHeading: ComponentType<any>;
  IndicatorsContainer: ComponentType<any>;
  IndicatorSeparator: ComponentType<any> | null;
  Input: ComponentType<any>;
  LoadingIndicator: ComponentType<any> | null;
  Menu: ComponentType<any>;
  MenuList: ComponentType<any>;
  MenuPortal: ComponentType<any>;
  LoadingMessage: ComponentType<any>;
  NoOptionsMessage: ComponentType<any>;
  MultiValue: ComponentType<any>;
  MultiValueContainer: ComponentType<any>;
  MultiValueLabel: ComponentType<any>;
  MultiValueRemove: ComponentType<any>;
  Option: ComponentType<any>;
  Placeholder: ComponentType<any>;
  SelectContainer: ComponentType<any>;
  SingleValue: ComponentType<any>;
  ValueContainer: ComponentType<any>;
}
export type SelectComponentsConfig = Partial<SelectComponents>;

type ThemeConfig = (theme: Theme) => Theme;

interface FormatOptionLabelMeta<V> {
  context: 'menu' | 'value';
  inputValue: string;
  selectValue: V;
}

export interface Props<IT, V, L> {
  /* Aria label (for assistive tech) */
  'aria-label'?: string;

  /* HTML ID of an element that should be used as the label (for assistive tech) */
  'aria-labelledby'?: string;

  /* Focus the control when it is mounted */
  autoFocus?: boolean;

  /* Remove the currently focused option when the user presses backspace */
  backspaceRemovesValue?: boolean;

  /* Remove focus from the input when the user selects an option (handy for dismissing the keyboard on touch devices) */
  blurInputOnSelect?: boolean;

  /* When the user reaches the top/bottom of the menu, prevent scroll on the scroll-parent  */
  captureMenuScroll?: boolean;

  /* Sets a className attribute on the outer component */
  className?: string;

  /*
    If provided, all inner components will be given a prefixed className attribute.
     This is useful when styling via CSS classes instead of the Styles API approach.
  */
  classNamePrefix?: string | null;

  /* Close the select menu when the user selects an option */
  closeMenuOnSelect?: boolean;

  /*
    If `true`, close the select menu when the user scrolls the document/body.
     If a function, takes a standard javascript `ScrollEvent` you return a boolean:
     `true` => The menu closes
     `false` => The menu stays open
     This is useful when you have a scrollable modal and want to portal the menu out,
    but want to avoid graphical issues.
   */
  closeMenuOnScroll?: boolean | EventListener;

  /*
    This complex object includes all the compositional components that are used
    in `react-select`. If you wish to overwrite a component, pass in an object
    with the appropriate namespace.
     If you only wish to restyle a component, we recommend using the `styles` prop
    instead. For a list of the components that can be passed in, and the shape
    that will be passed to them, see [the components docs](/components)
  */
  components?: SelectComponentsConfig;

  /* Whether the value of the select, e.g. SingleValue, should be displayed in the control. */
  controlShouldRenderValue?: boolean;

  /* Delimiter used to join multiple values into a single HTML Input value */
  delimiter?: string;

  /* Clear all values when the user presses escape AND the menu is closed */
  escapeClearsValue?: boolean;

  /* Custom method to filter whether an option should be displayed in the menu */
  filterOption?:
    | ((option: OptionType<IT, V, L>, inputValue: string) => boolean)
    | null;

  /*
    Formats group labels in the menu as React components
     An example can be found in the [Replacing builtins](/advanced#replacing-builtins) documentation.

     DISABLED as we never used
  */
  //formatGroupLabel?: typeof formatGroupLabel;

  /* Formats option labels in the menu and control as React components */
  formatOptionLabel?: (option: IT, meta: FormatOptionLabelMeta<V>) => ReactNode;

  /* Resolves option data to a string to be displayed as the label by components */
  getOptionLabel?: (item: IT) => L;

  /* Resolves option data to a string to compare options and specify value attributes */
  getOptionValue?: (item: IT) => V;

  /* Hide the selected option from the menu */
  hideSelectedOptions?: boolean;

  /* The id to set on the SelectContainer component. */
  id?: string;

  /* The value of the search input */
  inputValue?: string;

  /* The id of the search input */
  inputId?: string;

  /* Define an id prefix for the select components e.g. {your-id}-value */
  instanceId?: number | string;

  /* Is the select value clearable */
  isClearable?: boolean;

  /* Is the select disabled */
  isDisabled?: boolean;

  /* Is the select in a state of loading (async) */
  isLoading?: boolean;

  /*
    Override the built-in logic to detect whether an option is disabled
     An example can be found in the [Replacing builtins](/advanced#replacing-builtins) documentation.
  */
  isOptionDisabled?: (
    option: IT | string,
    options: Array<IT>
  ) => boolean | false;

  /* Override the built-in logic to detect whether an option is selected */
  isOptionSelected?: (
    option: OptionType<IT, V, L>,
    options: OptionsType<IT, V, L>
  ) => boolean;

  /* Support multiple selected options */
  isMulti?: boolean;

  /* Is the select direction right-to-left */
  isRtl?: boolean;

  /* Whether to enable search functionality */
  isSearchable?: boolean;

  /* Async: Text to display when loading options */
  loadingMessage?: (state: {inputValue: string}) => string | null;

  /* Minimum height of the menu before flipping */
  minMenuHeight?: number;

  /* Maximum height of the menu before scrolling */
  maxMenuHeight?: number;

  /* Whether the menu is open */
  menuIsOpen?: boolean;

  /* Default placement of the menu in relation to the control. 'auto' will flip
     when there isn't enough space below the control. */
  menuPlacement?: MenuPlacement;

  /* The CSS position value of the menu, when "fixed" extra layout management is required */
  menuPosition?: MenuPosition;

  /*
    Whether the menu should use a portal, and where it should attach
     An example can be found in the [Portaling](/advanced#portaling) documentation
  */
  menuPortalTarget?: HTMLElement;

  /* Whether to block scroll events when the menu is open */
  menuShouldBlockScroll?: boolean;

  /* Whether the menu should be scrolled into view when it opens */
  menuShouldScrollIntoView?: boolean;

  /* Name of the HTML Input (optional - without this, no input will be rendered) */
  name?: string;

  /* Text to display when there are no options */
  noOptionsMessage?: (inputValue: string) => React.ReactNode;

  /* Handle blur events on the control */
  onBlur?: FocusEventHandler;

  /* Handle change events on the select */
  onChange: (value: ValueType<IT>, meta: ActionMeta<IT>) => unknown;

  /* Handle focus events on the control */
  onFocus?: FocusEventHandler;

  /* Handle change events on the input */
  onInputChange?: (
    inputValue: string,
    inputActionMeta: InputActionMeta
  ) => void;

  /* Handle key down events on the select */
  onKeyDown?: KeyboardEventHandler;

  /* Handle the menu opening */
  onMenuOpen?: () => void;

  /* Handle the menu closing */
  onMenuClose?: () => void;

  /* Fired when the user scrolls to the top of the menu */
  onMenuScrollToTop?: (event: React.SyntheticEvent<HTMLElement>) => void;

  /* Fired when the user scrolls to the bottom of the menu */
  onMenuScrollToBottom?: (event: React.SyntheticEvent<HTMLElement>) => void;

  /* Allows control of whether the menu is opened when the Select is focused */
  openMenuOnFocus?: boolean;

  /* Allows control of whether the menu is opened when the Select is clicked */
  openMenuOnClick?: boolean;

  /* Array of options that populate the select menu */
  options: Array<IT>;

  /* Number of options to jump in menu when page{up|down} keys are used */
  pageSize?: number;

  /* Placeholder text for the select value */
  placeholder: string;

  /* Status to relay to screen readers */
  screenReaderStatus?: (state: {count: number}) => string;

  /*
    Style modifier methods
     A basic example can be found at the bottom of the [Replacing builtins](/advanced#replacing-builtins) documentation.
  */
  styles?: StylesConfig;

  /* Theme modifier method */
  theme?: ThemeConfig;

  /* Sets the tabIndex attribute on the input */
  tabIndex?: string;

  /* Select the currently focused option when the user presses tab */
  tabSelectsValue?: boolean;

  /* The value of the select; reflected by the selected option */
  value: IT | Array<IT> | null;

  /* A CSP Nonce which will be used in injected style sheets */
  nonce?: string;

  /* these ones are added by SelectBase */
  extraProps?: ExtraPropsType<IT>;
}

export function SweetReactSelect<IT, V = IT, L = ReactNode>({
  theme = themeModifier,
  styles = selectStyles,
  ...props
}: Props<IT, V, L>) {
  return <Select theme={theme} styles={styles} {...props} />;
}

export interface CreatableProps<IT, V = IT, L = ReactNode>
  extends Props<IT, V, L> {
  /* Allow options to be created while the `isLoading` prop is true. Useful to
     prevent the "create new ..." option being displayed while async results are
     still being loaded. */
  allowCreateWhileLoading?: boolean;

  /* Gets the label for the "create new ..." option in the menu. Is given the
     current input value. */
  formatCreateLabel?: (inputValue: string) => ReactNode;

  /* Determines whether the "create new ..." option should be displayed based on
     the current input value, select value and options array. */
  isValidNewOption?: (
    inputValue: string,
    value: Array<V>,
    options: Array<V>
  ) => boolean;

  /* Returns the data for the new option when it is created. Used to display the
     value, and is passed to `onChange`. */
  getNewOptionData?: (value: string, label: ReactNode) => any;

  /* If provided, this will be called with the input value when a new option is
     created, and `onChange` will **not** be called. Use this when you need more
     control over what happens when new options are created. */
  onCreateOption?: (inputValue: string) => unknown;

  /* Sets the position of the createOption element in your options list. Defaults to 'last' */
  createOptionPosition?: 'first' | 'last';
}

export function SweetCreatableReactSelect<IT, V = IT, L = ReactNode>({
  theme = themeModifier,
  styles = selectStyles,
  isValidNewOption = (q: string) => q !== '',
  ...props
}: CreatableProps<IT, V, L>) {
  return (
    <Creatable
      theme={theme}
      styles={styles}
      isValidNewOption={isValidNewOption}
      {...props}
    />
  );
}
